import {
  MultiFilterOperator,
  SearchZapsOrder,
  SearchZapsStatusWithAll,
} from "@zapier/bff";
import {
  isUntitledZap,
  normalizeZapNodeId,
  UNTITLED_ZAP_TITLE,
  ZAP_PAGE_SIZE,
} from "@zapier/utils";
import { useHydrateStorageZaps } from "./useHydrateStorageZaps";
import { SearchZap } from "./useQuerySearchZaps";
import { StorageZap, useQueryStorageZaps } from "./useQueryStorageZaps";
import { useMemo } from "react";
import { trpc } from "../trpc";
import { keepPreviousData } from "@tanstack/react-query";
import { useLimitOffsetFromPage } from "../../hooks/useLimitOffsetFromPage";
import type { Zap } from "./useSearchZaps";

interface Input {
  enabled?: boolean;
  order?: SearchZapsOrder;
  appId?: string;
  apps?: string;
  appsOperator?: MultiFilterOperator;
  page?: number | null;
  pageSize?: number;
  search?: string;
  status?: SearchZapsStatusWithAll;
  connection?: number;
  connections?: string;
}

interface Return {
  isLoading: boolean;
  isFetching: boolean;
  totalPages: number;
  totalCount: number;
  zaps: Zap[];
}

export function useDeactivatedUsersZaps({
  enabled = true,
  order = "-updated_at",
  appId = "",
  apps,
  appsOperator,
  connection,
  connections,
  page = 1,
  pageSize = ZAP_PAGE_SIZE,
  search = "",
  status = "all",
}: Input): Return {
  const { limit, offset } = useLimitOffsetFromPage(page ?? 1, pageSize);

  const useMultiApps = !!apps;
  const connectionsArgument =
    connections ?? (connection ? String(connection) : undefined);

  const zapsResponse = trpc.zap.deactivatedUsersZaps.useQuery(
    {
      limit,
      offset,
      order,
      ...(useMultiApps
        ? { apps, ...(appsOperator ? { appsOperator } : {}) }
        : appId
          ? { apps: appId }
          : {}),
      ...(connectionsArgument ? { connections: connectionsArgument } : {}),
      ...(status && status !== "all" ? { status } : {}),
      ...(search.length > 0 ? { search } : {}),
    },
    {
      enabled,
      placeholderData: keepPreviousData,
      trpc: {
        context: {
          skipBatching: true,
        },
      },
    },
  );

  const response = zapsResponse.data;
  const searchZaps = useMemo(() => {
    return response?.results || ([] as SearchZap[]);
  }, [response?.results]);

  const zapIds = searchZaps.map((zap: SearchZap) => zap.id);

  const { response: storageZaps = {} } = useQueryStorageZaps({ ids: zapIds });
  const storageZapList = useMemo(() => {
    const seen = new Set<string>();

    return Object.values(storageZaps).filter((zap) => {
      if (!zap?.id || seen.has(zap.id)) {
        return false;
      }

      seen.add(zap.id);
      return true;
    });
  }, [storageZaps]);
  const { list: _hydratedZaps, hash: hydratedZapsHash } = useHydrateStorageZaps(
    storageZapList,
    { membershipStatus: "all" },
  );

  const zaps = useMemo(() => {
    return searchZaps.map((zap: SearchZap) => {
      const hydratedZap: StorageZap | undefined =
        hydratedZapsHash[normalizeZapNodeId(zap.zapId)];

      return {
        ...zap,
        searchZap: zap,
        ...hydratedZap,
        id: zap.zapId,
        customuserId: hydratedZap?.customuserId ?? zap.ownerId,
        updatedAt: hydratedZap?.updatedAt ?? zap.updatedAt,
        title: isUntitledZap(hydratedZap?.title)
          ? zap.title || UNTITLED_ZAP_TITLE
          : hydratedZap?.title || zap.title || UNTITLED_ZAP_TITLE,
      };
    });
  }, [searchZaps, hydratedZapsHash]);

  const totalPages = response?.count ? Math.ceil(response.count / pageSize) : 1;

  return {
    totalPages,
    totalCount: response?.count || 0,
    zaps,
    isLoading: zapsResponse.isLoading,
    isFetching: zapsResponse.isFetching,
  };
}
